import React, { useEffect, useMemo, useState } from "react";
import { Autocomplete, Chip, TextField } from "@mui/material";
import { useTranslation } from "react-i18next";
import { jwtDecode } from "jwt-decode";
import { getResponsablesTodos } from "../../api/Proyectos/ProyectosTareasApi";
import { normalizeUserOptions } from "../../utils/meetingRecorder";
import { meetingAutocompleteSx } from "./meetingUiStyles";

function tokenUserOption() {
  try {
    const payload = jwtDecode(localStorage.getItem("token") || "");
    const id = Number(payload.userId ?? payload.id);
    const name = String(payload.username || "").trim();
    if (!Number.isFinite(id) || id <= 0 || !name) return null;
    return { id, name };
  } catch {
    return null;
  }
}

export default function MeetingAttendeeSelect({
  value = [],
  onChange,
  disabled = false,
  margin = "none",
}) {
  const { t } = useTranslation();
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const ids = Array.isArray(value)
    ? value.map(Number).filter((n) => n > 0)
    : [];

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getResponsablesTodos()
      .then((rows) => {
        if (!cancelled) setOptions(normalizeUserOptions(rows));
      })
      .catch(() => {
        if (!cancelled) setOptions([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const mergedOptions = useMemo(() => {
    const extra = tokenUserOption();
    if (!extra || options.some((o) => o.id === extra.id)) return options;
    return normalizeUserOptions([...options, extra]);
  }, [options]);

  const selected = useMemo(
    () => mergedOptions.filter((o) => ids.includes(o.id)),
    [mergedOptions, ids],
  );

  return (
    <Autocomplete
      sx={meetingAutocompleteSx}
      multiple
      options={mergedOptions}
      loading={loading}
      disabled={disabled}
      value={selected}
      onChange={(_, next) => onChange((next || []).map((n) => n.id))}
      getOptionLabel={(option) => option?.name || ""}
      isOptionEqualToValue={(a, b) => Number(a?.id) === Number(b?.id)}
      filterSelectedOptions
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => (
          <Chip
            {...getTagProps({ index })}
            key={option.id}
            label={option.name}
            size="small"
          />
        ))
      }
      renderInput={(params) => (
        <TextField
          {...params}
          margin={margin}
          label={t("Meetings.attendees")}
          placeholder={t("Meetings.attendeesPlaceholder")}
          fullWidth
        />
      )}
    />
  );
}
